import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import {
  MDBContainer,
  MDBRow,
  MDBCol,
  MDBCard,
  MDBCardBody,
  MDBBtn,
  MDBBtnGroup,
  MDBBadge,
  MDBIcon,
  MDBTypography
} from 'mdb-react-ui-kit';
import { ShootingSession, InventoryItem } from '../types';
import { apiService } from '../services/apiService';
import { inventoryService } from '../services/inventoryService';
import { useAuth } from '../contexts/AuthContext';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import './Sessions.css';

// Fix for default marker icon
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

L.Marker.prototype.options.icon = DefaultIcon;

interface SessionsProps {
  theme: 'light' | 'dark';
}

const Sessions: React.FC<SessionsProps> = ({ theme }) => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [sessions, setSessions] = useState<ShootingSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'list' | 'map'>('list');
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);

  // Create a map for quick inventory item lookup
  const inventoryMap = useMemo(() => {
    const map = new Map<string, InventoryItem>();
    inventoryItems.forEach(item => map.set(item.id, item));
    return map;
  }, [inventoryItems]);

  const loadInventory = useCallback(async () => {
    try {
      const items = await inventoryService.getInventory();
      setInventoryItems(items);
    } catch (error) {
      console.error('[Sessions] Failed to load inventory:', error);
    }
  }, []);

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      loadSessions();
      loadInventory();
    } else if (!authLoading && !isAuthenticated) {
      setLoading(false);
    }
  }, [authLoading, isAuthenticated, loadInventory]);

  const loadSessions = async () => {
    try {
      setLoading(true);
      const allSessions = await apiService.getAllSessions();
      setSessions(allSessions);
    } catch (error) {
      alert('Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSession = async (session: ShootingSession) => {
    if (window.confirm(`Are you sure you want to delete this session?\n\nDate: ${formatDate(session.date)}\nShots: ${session.shotCount}`)) {
      try {
        await apiService.deleteSession(session.id);
        await loadSessions();
      } catch (error) {
        alert('Failed to delete session');
      }
    }
  };

  const handleSessionClick = (sessionId: string) => {
    navigate(`/session-details/${sessionId}`);
  };

  const formatDate = (timestamp: number): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday = date.toDateString() === yesterday.toDateString();

    const timeStr = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });

    if (isToday) {
      return `Today at ${timeStr}`;
    } else if (isYesterday) {
      return `Yesterday at ${timeStr}`;
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }) + ` at ${timeStr}`;
    }
  };

  const formatDuration = (seconds?: number): string => {
    if (!seconds) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins > 0) {
      return `${mins}m ${secs}s`;
    }
    return `${secs}s`;
  };

  const sessionsWithLocation = sessions.filter(s => s.location);
  const totalSessions = sessions.length;
  const totalShots = sessions.reduce((sum, s) => sum + s.shotCount, 0);
  const totalScore = sessions.reduce((sum, s) => sum + s.totalScore, 0);

  return (
    <MDBContainer className="py-4">
      <MDBTypography tag="h1" className="text-center mb-4">Shooting Sessions</MDBTypography>

      {/* View Toggle */}
      <div className="d-flex justify-content-center mb-4">
        <MDBBtnGroup>
          <MDBBtn
            color={viewMode === 'list' ? 'primary' : 'light'}
            onClick={() => setViewMode('list')}
          >
            <MDBIcon fas icon="list" className="me-2" />
            List
          </MDBBtn>
          <MDBBtn
            color={viewMode === 'map' ? 'primary' : 'light'}
            onClick={() => setViewMode('map')}
          >
            <MDBIcon fas icon="map-marked-alt" className="me-2" />
            Map
          </MDBBtn>
        </MDBBtnGroup>
      </div>

      {/* Stats Summary */}
      <MDBCard className="mb-4">
        <MDBCardBody>
          <MDBRow className="text-center">
            <MDBCol md="4">
              <MDBTypography tag="h2" className="text-primary mb-1">{totalSessions}</MDBTypography>
              <MDBTypography tag="div" className="text-muted small">Total Sessions</MDBTypography>
            </MDBCol>
            <MDBCol md="4" className="border-start border-end">
              <MDBTypography tag="h2" className="text-primary mb-1">{totalShots}</MDBTypography>
              <MDBTypography tag="div" className="text-muted small">Total Shots</MDBTypography>
            </MDBCol>
            <MDBCol md="4">
              <MDBTypography tag="h2" className="text-primary mb-1">{totalScore.toFixed(1)}</MDBTypography>
              <MDBTypography tag="div" className="text-muted small">Total Score</MDBTypography>
            </MDBCol>
          </MDBRow>
        </MDBCardBody>
      </MDBCard>

      {viewMode === 'map' ? (
        // Map View
        <MDBCard>
          {sessionsWithLocation.length === 0 ? (
            <MDBCardBody className="text-center py-5">
              <MDBIcon fas icon="map-marker-alt" size="3x" className="text-muted mb-3" />
              <MDBTypography tag="h5">No sessions with location data</MDBTypography>
              <MDBTypography className="text-muted">Enable location tracking when starting sessions to see them on the map</MDBTypography>
            </MDBCardBody>
          ) : (
            <MapContainer
              center={[sessionsWithLocation[0].location!.latitude, sessionsWithLocation[0].location!.longitude]}
              zoom={13}
              style={{ height: '600px', borderRadius: '12px' }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {sessionsWithLocation.map((session) => (
                <Marker
                  key={session.id}
                  position={[session.location!.latitude, session.location!.longitude]}
                >
                  <Popup>
                    <div className="marker-popup">
                      <h4>{formatDate(session.date)}</h4>
                      <p>{session.shotCount} shots • Score: {session.totalScore.toFixed(1)}</p>
                      <button onClick={() => handleSessionClick(session.id)}>View Details</button>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          )}
        </MDBCard>
      ) : (
        // List View
        <div>
          {loading ? (
            <MDBCard>
              <MDBCardBody className="text-center py-5">
                <MDBIcon fas icon="spinner" spin size="2x" className="text-primary" />
                <MDBTypography className="mt-3">Loading sessions...</MDBTypography>
              </MDBCardBody>
            </MDBCard>
          ) : sessions.length === 0 ? (
            <MDBCard>
              <MDBCardBody className="text-center py-5">
                <MDBIcon fas icon="chart-bar" size="3x" className="text-muted mb-3" />
                <MDBTypography tag="h5">No sessions yet</MDBTypography>
                <MDBTypography className="text-muted">Complete a shooting session on the Range page to see it here</MDBTypography>
              </MDBCardBody>
            </MDBCard>
          ) : (
            sessions.map((session) => (
              <MDBCard
                key={session.id}
                className="mb-3 session-card"
                onClick={() => handleSessionClick(session.id)}
                style={{ cursor: 'pointer' }}
              >
                <MDBCardBody>
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div className="flex-grow-1">
                      <MDBTypography tag="h5" className="mb-2">
                        {formatDate(session.date)}
                        {session.sessionType === 'automatic' && (
                          <MDBBadge
                            color="success"
                            className="ms-2"
                          >
                            <MDBIcon fas icon="bullseye" className="me-1" />
                            Auto
                          </MDBBadge>
                        )}
                      </MDBTypography>
                    </div>
                    <MDBBtn
                      color="danger"
                      size="sm"
                      floating
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteSession(session);
                      }}
                    >
                      <MDBIcon fas icon="trash" />
                    </MDBBtn>
                  </div>

                  <MDBRow className="text-center py-3 border-top border-bottom">
                    <MDBCol>
                      <MDBTypography className="text-muted small mb-1">Shots</MDBTypography>
                      <MDBTypography tag="h4" className="mb-0">{session.shotCount}</MDBTypography>
                    </MDBCol>
                    <MDBCol>
                      <MDBTypography className="text-muted small mb-1">Total Score</MDBTypography>
                      <MDBTypography tag="h4" className="mb-0">{session.totalScore.toFixed(1)}</MDBTypography>
                    </MDBCol>
                  </MDBRow>

                  {session.location?.address && (
                    <MDBTypography className="text-muted small mt-3">
                      <MDBIcon fas icon="map-marker-alt" className="me-2" />
                      {session.location.address}
                    </MDBTypography>
                  )}

                  {session.inventoryItemId && inventoryMap.get(session.inventoryItemId) && (
                    <MDBTypography className="text-muted small mt-2">
                      <MDBIcon fas icon="crosshairs" className="me-2" />
                      {inventoryMap.get(session.inventoryItemId)?.name}
                    </MDBTypography>
                  )}
                </MDBCardBody>
              </MDBCard>
            ))
          )}
        </div>
      )}
    </MDBContainer>
  );
};

export default Sessions;
